import { gql } from 'graphql-tag';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
// Values come from backend-configured contact types, not static enum options
import { useAppContext } from '~/App';
import { useAbilityContext } from '~/components/Can';
import { NavItemsBar } from '~/components/nav/NavItemsBar';
import { NavSettingsPanel } from '~/components/ui/NavSettingsPanel';
import { TopBar } from '~/components/ui/TopBar';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useContacts } from '~/routes/resources/contacts';
import { buildNavItems } from '~/utils/nav/buildNavItems';

const ALL = 'All' as const;
const OTHER = 'Other' as const;
const EVT_CONTACT_TYPE_RENAMED = 'contactTypeRenamed';
const STORAGE_KEYS = {
  view: 'nav.contacts.view',
  settings: 'nav.contacts.settings',
  nav: 'nav.contacts.items',
  showAllNormalized: (tenantKey: string) =>
    `tid:${tenantKey}:contactsNavShowAll`,
  showAllLegacy: (tenantKey: string) =>
    `config.contactsNavShowAll.${tenantKey}`,
} as const;

// Contact nav + types config query
const CONTACT_NAV_CONFIG_Q = gql`
  query ContactNavConfig_Nav {
    contactTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
    }
  }
`;

// Preset contact types (used for defaults when nav is not customized)
const CONTACT_TYPES_PRESET_Q = gql`
  query ContactTypesPreset_Nav($preset: Boolean) {
    contactTypes(preset: $preset)
  }
`;

export type ContactsNavProps = {
  onViewChange?: (view: string) => void;
  initialView?: string;
  onNavItemsChange?: (items: string[]) => void;
};

export function ContactsNav({
  onViewChange,
  initialView,
  onNavItemsChange,
}: ContactsNavProps = {}) {
  const { t } = useTranslation();
  const localStorage = useLocalStorage();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useAppContext();
  const ability = useAbilityContext();
  const tenantKey = tenant?.id || 'default';
  const { isMobile } = useBreakpoint();
  const showAllKeyNormalized = STORAGE_KEYS.showAllNormalized(tenantKey);
  const showAllKeyLegacy = STORAGE_KEYS.showAllLegacy(tenantKey);
  const [view, setView] = useState(initialView ?? '');
  const isOwner = ability.can('manage', 'Account');

  // Desktop settings open/close state (persisted)
  const [showSettings, setShowSettings] = useState<boolean>(() => {
    try {
      const saved = localStorage.get(STORAGE_KEYS.settings) as string | null;
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const setShowSettingsAndSave = useCallback(
    (newValue: boolean) => {
      setShowSettings(newValue);
      try {
        localStorage.set(STORAGE_KEYS.settings, newValue.toString());
      } catch (error) {
        console.warn(
          'Failed to persist contacts settings open state to localStorage',
          error
        );
      }
    },
    [localStorage]
  );

  const [cfgResult] = useQuery({
    query: CONTACT_NAV_CONFIG_Q,
    requestPolicy: 'cache-and-network',
  });

  // Load preset contact types to power default nav when not customized
  const [presetResult] = useQuery({
    query: CONTACT_TYPES_PRESET_Q,
    variables: { preset: true },
    requestPolicy: 'cache-and-network',
  });

  const [showAllOverride, setShowAllOverride] = useState<boolean | null>(null);

  // Whether the "All" tab is enabled (server config possibly overridden locally)
  const allEnabled = useMemo<boolean>(() => {
    const serverShowAll: boolean =
      cfgResult.data?.contactTypeConfig?.showAllInNav ?? true;
    return showAllOverride == null ? serverShowAll : Boolean(showAllOverride);
  }, [cfgResult.data, showAllOverride]);

  // Determine if any contacts have no relationship to decide whether to show "Other"
  // Use lightweight boolean from server instead of loading full list
  const CONTACTS_RELLESS_Q = gql`
    query ContactsRelless_Nav {
      hasUntypedContacts
      contactTypesInUse
    }
  `;
  const [rellessResult] = useQuery<{
    hasUntypedContacts: boolean;
    contactTypesInUse: string[];
  }>({
    query: CONTACTS_RELLESS_Q,
    requestPolicy: 'cache-and-network',
  });

  // Fallback: directly inspect contacts to infer untyped presence and derive types in use
  const contacts = useContacts({
    includeInactive: true,
    requestPolicy: 'cache-first',
  });
  const hasUntypedFromContacts = useMemo(
    () => contacts.some((c) => (c.relationship ?? []).length === 0),
    [contacts]
  );

  // Derive types in use from actual contact data (fallback for when server query returns empty)
  const typesInUseFromContacts = useMemo(() => {
    const allRelationships: string[] = [];
    contacts.forEach((c) => {
      const rels = c.relationship ?? [];
      allRelationships.push(
        ...rels.filter((r): r is string => !!r && r.trim() !== '')
      );
    });
    // Remove duplicates and sort
    const unique = Array.from(
      new Set(allRelationships.filter((t) => t.toLowerCase() !== 'other'))
    );
    const result = unique.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );

    return result;
  }, [contacts]);

  useEffect(() => {
    // Read saved local override when tenant changes
    try {
      const v =
        (localStorage.get(showAllKeyNormalized) as string | null) ??
        (localStorage.get(showAllKeyLegacy) as string | null);
      if (v === 'true' || v === 'false') setShowAllOverride(v === 'true');
    } catch {
      setShowAllOverride(null);
    }
  }, [showAllKeyNormalized, showAllKeyLegacy, localStorage]);

  useEffect(() => {
    const handler = () => {
      // Re-read local storage on event to get latest value
      try {
        const v =
          (localStorage.get(showAllKeyNormalized) as string | null) ??
          (localStorage.get(showAllKeyLegacy) as string | null);
        if (v === 'true' || v === 'false') setShowAllOverride(v === 'true');
      } catch (error) {
        console.warn(
          'contactsNavShowAllChanged: failed to read showAll override',
          error
        );
      }
    };
    window.addEventListener(
      'contactsNavShowAllChanged',
      handler as EventListener
    );
    return () =>
      window.removeEventListener(
        'contactsNavShowAllChanged',
        handler as EventListener
      );
  }, [showAllKeyNormalized, showAllKeyLegacy, localStorage]);

  const navItems = useMemo<string[]>(() => {
    const configuredValues: string[] = (
      cfgResult.data?.contactTypeConfig?.types ?? []
    )
      .map((v: unknown) => String(v))
      .filter(Boolean);
    const savedNav: string[] = (
      cfgResult.data?.contactTypeConfig?.navTypes ?? []
    )
      .map((v: unknown) => String(v))
      .filter(Boolean);
    const presets: string[] = (presetResult.data?.contactTypes ?? [])
      .map((v: unknown) => String(v))
      .filter(Boolean);
    const typesInUseRaw: string[] = (
      rellessResult.data?.contactTypesInUse ?? []
    ).map((v: unknown) => String(v));
    const hasEmptyType = typesInUseRaw.some((v) => v.trim() === '');
    const typesInUseFromServer: string[] = typesInUseRaw.filter(
      (v) => v.trim() !== ''
    );

    // Combine server-provided types with locally-derived types from actual contacts
    const typesInUseCombined = Array.from(
      new Set([...typesInUseFromServer, ...typesInUseFromContacts])
    );
    const typesInUse: string[] = typesInUseCombined.filter(
      (v) => v.trim() !== ''
    );

    const hasUntyped =
      rellessResult.data?.hasUntypedContacts === true ||
      hasEmptyType ||
      hasUntypedFromContacts;

    const orderMode =
      (cfgResult.data?.contactTypeConfig?.orderMode as 'ALPHA' | 'MANUAL') ||
      'ALPHA';
    const result = buildNavItems({
      savedNav,
      configuredValues,
      presets,
      typesInUse,
      hasUntyped,
      showAllInNav: allEnabled,
      orderMode,
      topN: 6,
    });

    return result;
  }, [
    cfgResult.data?.contactTypeConfig?.types,
    cfgResult.data?.contactTypeConfig?.orderMode,
    cfgResult.data?.contactTypeConfig?.navTypes,
    presetResult.data?.contactTypes,
    rellessResult.data?.contactTypesInUse,
    rellessResult.data?.hasUntypedContacts,
    hasUntypedFromContacts,
    typesInUseFromContacts,
    allEnabled,
  ]);

  // Stable displayed items (prevents jump/flicker); synced after server updates
  // Initialize from localStorage for instant display, fallback to [ALL]
  const [displayedNavItems, setDisplayedNavItems] = useState<string[]>(() => {
    try {
      const raw = localStorage.get(STORAGE_KEYS.nav) as string | null;
      const parsed = raw ? (JSON.parse(raw) as unknown) : null;
      if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
        return parsed as string[];
      }
    } catch {
      // ignore
    }
    // Safe minimal fallback to avoid empty UI while queries load
    return [ALL];
  });

  // Sync computed nav items to displayed when they change
  useEffect(() => {
    if (!navItems || navItems.length === 0) return;
    setDisplayedNavItems((prev) =>
      prev.join('|') === navItems.join('|') ? prev : navItems
    );
    // Persist for next load
    try {
      localStorage.set(STORAGE_KEYS.nav, JSON.stringify(navItems));
    } catch {
      // ignore
    }
  }, [navItems, localStorage]);

  useEffect(() => {
    onNavItemsChange?.(displayedNavItems);
  }, [displayedNavItems, onNavItemsChange]);

  const updateView = useCallback(
    (v: string) => {
      setView(v);
      onViewChange?.(v);
      try {
        localStorage.set(STORAGE_KEYS.view, v);
      } catch (error) {
        console.warn('Failed to persist contacts view to localStorage', error);
      }
      if (!location.pathname.startsWith('/contacts')) {
        navigate('/contacts');
      }
    },
    [onViewChange, localStorage, location.pathname, navigate]
  );

  // Keep current tab when a type is renamed elsewhere
  useEffect(() => {
    const onRenamed = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { from?: string; to?: string }
        | undefined;
      if (!detail) return;
      const { from, to } = detail;
      if (!from || !to) return;

      // Update view if it matches the renamed type
      if (view === from) {
        setView(to);
        onViewChange?.(to);
        try {
          localStorage.set(STORAGE_KEYS.view, to);
        } catch (error) {
          console.warn(
            'Failed to persist contacts view to localStorage',
            error
          );
        }
        if (!location.pathname.startsWith('/contacts')) {
          navigate('/contacts');
        }
      }
    };
    window.addEventListener(
      EVT_CONTACT_TYPE_RENAMED,
      onRenamed as EventListener
    );
    return () =>
      window.removeEventListener(
        EVT_CONTACT_TYPE_RENAMED,
        onRenamed as EventListener
      );
  }, [view, onViewChange, localStorage, location.pathname, navigate]);

  // Navigate to first tab when current tab is deleted
  useEffect(() => {
    const onDeleted = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { deletedTypes?: string[] }
        | undefined;
      if (!detail?.deletedTypes || detail.deletedTypes.length === 0) return;

      // If the current view was deleted, navigate to the first available tab
      if (detail.deletedTypes.includes(view)) {
        if (displayedNavItems.length > 0) {
          const firstTab = displayedNavItems[0];
          updateView(firstTab);
        }
      }
    };
    window.addEventListener('contactTypeDeleted', onDeleted as EventListener);
    return () =>
      window.removeEventListener(
        'contactTypeDeleted',
        onDeleted as EventListener
      );
  }, [view, displayedNavItems, updateView]);

  useEffect(() => {
    if (!view && displayedNavItems.length > 0) {
      try {
        const saved =
          (localStorage.get(STORAGE_KEYS.view) as string | null) ?? '';
        const v =
          saved && displayedNavItems.includes(saved)
            ? saved
            : displayedNavItems[0];
        setView(v);
        onViewChange?.(v);
      } catch {
        setView(displayedNavItems[0]);
        onViewChange?.(displayedNavItems[0]);
      }
    }
  }, [view, displayedNavItems, localStorage, onViewChange]);

  // If "All" is not enabled and the current view is invalid (including "All"),
  // default to the first available tab.
  useEffect(() => {
    if (!allEnabled && displayedNavItems.length > 0) {
      if (view === ALL || (view && !displayedNavItems.includes(view))) {
        updateView(displayedNavItems[0]);
      }
    }
  }, [allEnabled, displayedNavItems, view, updateView]);

  const labelFor = useCallback(
    (v: string) => {
      if (v === ALL) return t('all');
      if (v === OTHER) {
        const key = 'contactRelationships.Other';
        const translated = t(key);
        return translated === key ? OTHER : translated;
      }
      // Only translate when value is part of presets; otherwise render verbatim
      const presetList: string[] = (presetResult.data?.contactTypes ?? []).map(
        (x: unknown) => String(x)
      );
      const presetSet = new Set<string>(presetList);
      if (presetSet.has(v)) {
        const key = `contactRelationships.${v}`;
        const translated = t(key);
        if (translated !== key) return translated;
        // Fallback: try a compacted key without spaces (e.g., "ServiceProvider")
        const compact = v.replace(/\s+/g, '');
        const compactKey = `contactRelationships.${compact}`;
        const translatedCompact = t(compactKey);
        return translatedCompact === compactKey ? v : translatedCompact;
      }
      return v;
    },
    [t, presetResult.data?.contactTypes]
  );

  return (
    <>
      {isMobile ? (
        <TopBar
          title={
            view === ALL
              ? t('myContacts', { defaultValue: 'My Contacts' })
              : view
              ? labelFor(view)
              : t('contact_plural')
          }
          menuItems={[
            ...displayedNavItems.map((rel) => ({
              key: `contact-rel-${rel}`,
              label:
                rel === ALL
                  ? t('allContacts', {
                      defaultValue: `All ${t('contact_plural')}`,
                    })
                  : labelFor(rel),
              isActive: rel === view,
              onClick: () => updateView(rel),
            })),
          ]}
        />
      ) : (
        <NavItemsBar
          items={displayedNavItems}
          activeItem={view}
          onSelect={updateView}
          labelFor={labelFor}
          leftSlot={undefined}
          recomputeKey={showSettings}
          isPanelOpen={isOwner ? showSettings : undefined}
          rightSlot={
            isOwner ? (
              <NavSettingsPanel
                isOpen={showSettings}
                onToggle={() => setShowSettingsAndSave(!showSettings)}
                menuItems={[
                  {
                    key: 'contacts-settings',
                    label: 'Contact Types',
                    to: '#org-settings-open:contactTypes',
                    showPopupIcon: true,
                  },
                ]}
                hasItems={true}
              />
            ) : null
          }
          farRightSlot={
            <Link className='add-button' to='new'>
              + {t('contact')}
            </Link>
          }
        />
      )}
    </>
  );
}
